import { ReactComponent as ExternalLinkSvg } from '@frontend/icons/external-link-2.svg';
import { ReactComponent as StarSvg } from '@frontend/icons/star.svg';
import {
  BoxStyleProps,
  Button,
  Center,
  EmojiFeedback,
  EmojiFeedbackOption,
  Icon,
} from '@frontend/ui-library';
import { getIdFromUrl } from '@frontend/utils';
import { motion } from 'framer-motion';
import { useRef, useState } from 'react';
import { useSendAnalytics } from '../../hooks';
import links from '../../constants/links';

interface SatisfactionRatingProps extends BoxStyleProps {
  className?: string;
  rating?: string;
}

const FEEDBACK_OPTIONS = [
  {
    id: 'issue',
    value: '1',
    emoji: '😔',
    label: 'I had issues',
  },
  {
    id: 'fine',
    value: '2',
    emoji: '🙂',
    label: 'It was fine',
  },
  {
    id: 'loved',
    value: '3',
    emoji: '😍',
    label: 'Loved it!',
  },
] as const;

export default function SatisfactionRating({
  className,
  rating,
  ...boxStyleProps
}: SatisfactionRatingProps): JSX.Element {
  const { sendAnalytics } = useSendAnalytics();
  const [selectedRating, setSelectedRating] = useState<string>(rating || '');
  const shouldAnimateRef = useRef<boolean>(rating ? false : true);

  const handleSelect = (rating: string) => {
    setSelectedRating(rating);
    sendAnalytics({
      variables: {
        id: getIdFromUrl(),
        type: 'bill',
        eventName: 'ratings',
        data: JSON.stringify({ rating }),
      },
    });
  };

  const selectedOption = selectedRating
    ? FEEDBACK_OPTIONS.find((option) => option.value === selectedRating)
    : null;

  return (
    <EmojiFeedback
      title="How was your experience?"
      className={className}
      selectedValue={selectedRating}
      onSelect={handleSelect}
      options={
        <Center>
          {selectedRating && selectedOption ? (
            <EmojiFeedbackOption
              id={selectedOption.id}
              value={selectedOption.value}
              emoji={selectedOption.emoji}
              label={selectedOption.label}
              selected={true}
              shouldAnimate={shouldAnimateRef.current}
            />
          ) : (
            FEEDBACK_OPTIONS.map((option) => (
              <EmojiFeedbackOption
                key={option.id}
                id={option.id}
                value={option.value}
                emoji={option.emoji}
                label={option.label}
                selected={false}
                shouldAnimate={shouldAnimateRef.current}
                onSelect={handleSelect}
              />
            ))
          )}
        </Center>
      }
      {...boxStyleProps}
    >
      {selectedRating && (
        <FeedbackContent
          value={selectedRating}
          shouldAnimate={shouldAnimateRef.current}
        />
      )}
    </EmojiFeedback>
  );
}

function FeedbackContent({
  value,
  shouldAnimate,
}: {
  value: string;
  shouldAnimate: boolean;
}): JSX.Element {
  const isPositiveFeedback = value === '3';
  const linkProps = {
    href: isPositiveFeedback
      ? links.TRUSTPILOT_LINK
      : links.getUserFeedbackFormLink(getIdFromUrl()),
    text: isPositiveFeedback
      ? 'rate us on TrustPilot'
      : 'Tell us about your experience',
  };

  return (
    <motion.div
      initial={shouldAnimate ? { y: 56 } : {}}
      animate={shouldAnimate ? { y: 0 } : {}}
      transition={{ duration: 0.4, ease: 'easeOut' }}
    >
      <Button
        as="a"
        href={linkProps.href}
        target="_blank"
        mt="8"
        variant="ghost"
        size="small"
        rightIcon={
          isPositiveFeedback ? (
            <Icon component={<StarSvg />} />
          ) : (
            <Icon component={<ExternalLinkSvg />} />
          )
        }
      >
        {linkProps.text}
      </Button>
    </motion.div>
  );
}
